import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Download,
  Eye,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { api, unduhBerkas } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/States';
import {
  Pagination,
  Table,
  TableWrapper,
  Td,
  Th,
  Tr,
} from '@/components/ui/Table';
import { formatDomain, formatNumber, formatRelative } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';

const STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'draft', label: 'Draf' },
  { value: 'scheduled', label: 'Terjadwal' },
  { value: 'queued', label: 'Mengantre' },
  { value: 'running', label: 'Berjalan' },
  { value: 'completed', label: 'Selesai' },
  { value: 'failed', label: 'Gagal' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Terbaru' },
  { value: 'lastRun', label: 'Terakhir dijalankan' },
  { value: 'name', label: 'Nama A–Z' },
  { value: 'oldest', label: 'Terlama' },
];

/** Layar "List Job": tabel seluruh job dengan pencarian dan filter. */
export const JobListPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState(null);

  const hapusJob = useMutation({
    mutationFn: (id) => api.delete(`/jobs/${id}`),
    onSuccess: () => {
      toast.success('Job dihapus', {
        description: 'Jadwalnya ikut berhenti. Data hasil crawl yang sudah tersimpan tetap ada.',
      });
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (err) => {
      toast.error('Gagal menghapus', { description: err?.message });
      setPendingDelete(null);
    },
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  // Pencarian ditunda 350 ms supaya tidak menembak API pada setiap ketikan.
  const debouncedSearch = useDebounced(search, 350);

  const page = Number(searchParams.get('page') ?? 1);
  const status = searchParams.get('status') ?? '';
  const sort = searchParams.get('sort') ?? 'newest';

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Mengubah filter selalu mengembalikan ke halaman pertama - kalau tidak,
    // pengguna bisa mendarat di halaman 7 dari hasil yang cuma 2 halaman.
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['jobs', { page, status, sort, search: debouncedSearch }],
    queryFn: () =>
      api.get('/jobs', { params: { page, limit: 15, status, sort, search: debouncedSearch } }),
    placeholderData: (previous) => previous,
  });

  const jobs = data?.data ?? [];
  const meta = data?.meta;
  const isFiltered = Boolean(status || debouncedSearch);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              updateParam('search', e.target.value);
            }}
            placeholder="Cari job atau domain…"
            iconLeft={Search}
            aria-label="Cari job"
            className="sm:max-w-xs"
          />

          <Select
            value={status}
            onChange={(e) => updateParam('status', e.target.value)}
            options={STATUS_OPTIONS}
            aria-label="Filter status"
            className="sm:w-44"
          />

          <Select
            value={sort}
            onChange={(e) => updateParam('sort', e.target.value)}
            options={SORT_OPTIONS}
            aria-label="Urutkan"
            className="sm:w-48"
          />
        </div>

        <Button icon={Plus} onClick={() => navigate('/jobs/new')} className="shrink-0">
          Crawl Baru
        </Button>
      </div>

      <Card className="overflow-hidden">
        {isError ? (
          <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
        ) : isLoading ? (
          <TableSkeleton rows={8} columns={6} />
        ) : jobs.length === 0 ? (
          isFiltered ? (
            <EmptyState
              icon={SlidersHorizontal}
              title="Tidak ada job yang cocok"
              description="Coba kata kunci lain, atau lepaskan saringan yang sedang aktif."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('');
                    setSearchParams({}, { replace: true });
                  }}
                >
                  Hapus semua filter
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Database}
              title="Belum ada job scraping"
              description="Satu job berisi alamat halaman yang ingin diambil, kolom apa saja yang dikumpulkan, dan jadwalnya kalau memang perlu berulang. Buat yang pertama untuk mencoba."
              action={
                <Button icon={Plus} onClick={() => navigate('/jobs/new')}>
                  Buat Job Pertama
                </Button>
              }
            />
          )
        ) : (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Nama Job</Th>
                    <Th className="hidden md:table-cell">Sumber URL</Th>
                    <Th align="right" className="hidden sm:table-cell">
                      Baris
                    </Th>
                    <Th className="hidden lg:table-cell">Terakhir Dijalankan</Th>
                    <Th>Status</Th>
                    <Th align="right">Aksi</Th>
                  </tr>
                </thead>

                <tbody>
                  {jobs.map((job) => (
                    <Tr key={job.id} onClick={() => navigate(`/jobs/${job.id}`)}>
                      <Td>
                        <p className="font-semibold text-[var(--text-primary)]">{job.name}</p>
                        <p className="mt-0.5 truncate text-xs text-[var(--text-muted)] md:hidden">
                          {formatDomain(job.targetUrl)}
                        </p>
                      </Td>

                      <Td className="hidden md:table-cell">
                        <span className="text-[var(--text-muted)]">
                          {formatDomain(job.targetUrl)}
                        </span>
                      </Td>

                      <Td align="right" className="hidden tabular sm:table-cell">
                        {formatNumber(job.stats?.totalRecords ?? 0)}
                      </Td>

                      <Td className="hidden lg:table-cell">
                        {formatRelative(job.stats?.lastRunAt)}
                      </Td>

                      <Td>
                        <StatusBadge status={job.status} size="sm" />
                      </Td>

                      <Td align="right">
                        {/* Klik pada tombol tidak boleh ikut memicu klik baris. */}
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link to={`/jobs/${job.id}`}>
                            <Button
                              variant="ghost"
                              size="xs"
                              iconOnly
                              aria-label={`Lihat detail ${job.name}`}
                            >
                              <Eye className="size-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="xs"
                            iconOnly
                            aria-label={`Unduh hasil ${job.name}`}
                            // Tombol di sebelahnya punya tooltip, yang ini
                            // tidak, jadi ikonnya menjadi teka-teki. Judulnya
                            // menyebut apa yang diunduh, bukan sekadar
                            // "unduh".
                            title={
                              job.stats?.totalRecords
                                ? `Unduh ${job.stats.totalRecords.toLocaleString('id-ID')} baris sebagai CSV`
                                : 'Belum ada data untuk diunduh'
                            }
                            disabled={!job.stats?.totalRecords}
                            onClick={() =>
                              unduhBerkas(
                                `/jobs/${job.id}/export?format=csv`,
                                `${job.name}.csv`,
                              ).catch((err) =>
                                toast.error('Ekspor gagal', { description: err.message }),
                              )
                            }
                          >
                            <Download className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            iconOnly
                            aria-label={`Hapus ${job.name}`}
                            title="Hapus job"
                            className="hover:text-danger-600"
                            onClick={() => setPendingDelete(job)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>

            <div className="border-t border-[var(--border-subtle)]">
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={meta.total}
                limit={meta.limit}
                onPageChange={(next) => updateParam('page', String(next))}
              />
            </div>
          </>
        )}
      </Card>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => hapusJob.mutate(pendingDelete.id)}
        loading={hapusJob.isPending}
        destructive
        title={`Hapus job "${pendingDelete?.name}"?`}
        description="Job dan jadwalnya dihapus. Data hasil crawl yang sudah tersimpan di Penyimpanan Pribadi tidak ikut terhapus."
        confirmLabel="Ya, hapus job"
      />
    </div>
  );
};
